import * as Sentry from '@sentry/react';
import { message } from 'antd';
import moment from 'moment';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  AiOutlineCalendar,
  AiOutlineDownload,
  AiOutlineDollarCircle,
  AiOutlineFolder,
} from 'react-icons/ai';
import { HiOutlineExternalLink } from 'react-icons/hi';
import { BsCalendarCheck } from 'react-icons/bs';

import { LOCAL_STORAGE } from '../../../../constants/local-storage.constant';
import { brlCurrencyFormat } from '../../../../helpers/brlCurrencyFormat';
import {
  forceFileDownload,
  forceFileDownloadAlternative,
} from '../../../../helpers/forceFileDownload';
import { getDepartmentName } from '../../../../helpers/getDepartment';
import { getMimeType } from '../../../../helpers/getMimeType';
import { isPaymentRequired } from '../../../../helpers/isPaymentRequired';
import { logger } from '../../../../helpers/logger';
import {
  checkNetworkConnectivity,
  getNetworkErrorMessage,
  isNetworkError,
} from '../../../../helpers/networkUtils';
import { useAuth } from '../../../../hooks/useAuth';
import { downloadFile, markFileAsViewed } from '../../../../operations/file';
import { ObligationFile } from '../../../../operations/file/types';
import {
  AlreadyDownloadedText,
  DownloadButton,
  DownloadContainer,
  ErrorInstructions,
  ErrorMessage,
  ErrorTitle,
  InfoContent,
  InfoGrid,
  InfoIcon,
  InfoItem,
  InfoLabel,
  InfoValue,
  ModalStyled,
  OpenFileButton,
  WhatsappContainer,
  WhatsappImage,
  WhatsappLink,
  WhatsappText,
} from './styles';

interface ObligationModalProps {
  isVisible: boolean;
  onClose: () => void;
  data: ObligationFile;
  // eslint-disable-next-line no-unused-vars
  updateFiles: (updatedFile: ObligationFile) => void;
  setModalData: Dispatch<SetStateAction<ObligationFile>>;
  // eslint-disable-next-line no-unused-vars
  openFeedbackModal: (file: ObligationFile) => void;
}

export const ObligationModal = ({
  isVisible,
  onClose,
  data,
  updateFiles,
  setModalData,
  openFeedbackModal,
}: ObligationModalProps) => {
  const { user } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const logErrorToSentry = (
    error: any,
    additionalTags: Record<string, string> = {},
    additionalExtras: Record<string, any> = {}
  ) => {
    Sentry.withScope((scope) => {
      scope.setTag('component', 'ObligationModal');
      scope.setTag('action', 'downloadFile');
      scope.setTag('isNetworkError', isNetworkError(error));
      scope.setExtra('fileId', data.id);
      scope.setExtra('fileDescription', data.description);
      scope.setExtra('fileUrl', data.file_url);
      scope.setExtra('userId', user?.id);
      scope.setExtra('department', data.department);

      // Add additional tags and extras
      Object.entries(additionalTags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });

      Object.entries(additionalExtras).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });

      scope.setLevel('error');
      Sentry.captureException(error);
    });
  };

  const handleModalClose = () => {
    setDownloadFailed(false);
    onClose();
  };

  // Marca como visualizado quando o arquivo é entregue por um caminho que não
  // passa pelo endpoint de download (ex.: fallback de abrir em nova aba)
  const registerViewFallback = () => {
    markFileAsViewed(data.id).catch((error) => {
      logger.error('Error marking file as viewed (fallback):', error);
      logErrorToSentry(error, {
        action: 'markFileAsViewedFallback',
      });
    });

    const viewedFile = {
      ...data,
      viewed_at: moment().format('L LTS'),
      viewed_by: user!.id,
    };
    updateFiles(viewedFile);
    setModalData(viewedFile);
  };

  const handleDownloadFile = async () => {
    setIsDownloading(true);
    logger.log('handleDownloadFile', data);
    
    try {
      // Check network connectivity first
      const networkCheck = await checkNetworkConnectivity();
      if (!networkCheck.isOnline) {
        throw new Error('Sem conexão com a internet. Verifique sua rede e tente novamente.');
      }

      logger.log('Downloading file...');
      
      // Direct download without retry mechanism
      const file = await downloadFile(data.id);

      updateFiles({
        ...data,
        viewed_at: moment().format('L LTS'),
        viewed_by: user!.id,
      });
      setModalData((drawerData) => ({
        ...drawerData,
        viewed_at: moment().format('L LTS'),
        viewed_by: user!.id,
      }));

      const feedbackHasBeenDisplayed =
        localStorage.getItem(LOCAL_STORAGE.FEEDBACK_HAS_BEEN_DISPLAYED) ===
        'true';

      if (!data.viewed_at && !feedbackHasBeenDisplayed) {
        openFeedbackModal(data);
        localStorage.setItem(LOCAL_STORAGE.FEEDBACK_HAS_BEEN_DISPLAYED, 'true');
      }

      const extension = data.file_url.split('.').pop();

      const downloadSuccess = await forceFileDownload({
        file,
        type: getMimeType(extension),
        filename: `${data.description} ${moment().format(
          'DD/MM/YYYY_H:m:s'
        )}.${extension}`,
      });

      if (!downloadSuccess) {
        // If the primary download method failed, try alternative method
        const alternativeSuccess = await forceFileDownloadAlternative({
          file,
          type: getMimeType(extension),
          filename: `${data.description} ${moment().format(
            'DD/MM/YYYY_H:m:s'
          )}.${extension}`,
        });

        if (!alternativeSuccess) {
          throw new Error(
            'Navegador bloqueou o download - verifique as configurações do seu navegador ou tente salvar manualmente o arquivo'
          );
        }
      }

      // Reset download failed state on success
      setDownloadFailed(false);
      
      message.success('Arquivo baixado com sucesso!');
    } catch (error) {
      // Hide download button on any failure
      setDownloadFailed(true);
      
      const isCurrentErrorNetwork = isNetworkError(error);

      // Log error to Sentry with detailed context
      logger.error('File download error:', error);
      logErrorToSentry(error, {
        errorType: isCurrentErrorNetwork ? 'network' : 'other',
      });

      // Handle network errors differently
      if (isCurrentErrorNetwork) {
        // Also try to open file in new tab automatically for network errors
        try {
          if (data.file_url) {
            window.open(data.file_url, '_blank');
            registerViewFallback();
            message.info(
              'Arquivo aberto em nova aba como alternativa ao download. (E.1)'
            );
          }
        } catch (fallbackError) {
          logger.error('Failed to open file in new tab after network error:', fallbackError);
          message.error(
            'Não foi possível abrir o arquivo. Entre em contato com o suporte. (E.2)'
          );
          logErrorToSentry(fallbackError, {
            action: 'openFileInNewTab',
            context: 'networkErrorFallback',
          });
        }
      } else {
        // Try to open file in new tab as fallback for non-network errors
        try {
          if (data.file_url) {
            window.open(data.file_url, '_blank');
            registerViewFallback();
            message.info(
              'Arquivo aberto em nova aba como alternativa ao download.'
            );
          }
        } catch (fallbackError) {
          message.error(
            'Não foi possível abrir o arquivo. Entre em contato com o suporte.'
          );
          logger.error('Failed to open file in new tab:', fallbackError);
          logErrorToSentry(fallbackError, {
            action: 'openFileInNewTab',
          });
        }

        const errorMessage =
          error instanceof Error &&
          error.message.includes('Navegador bloqueou o download')
            ? 'O navegador bloqueou o download. Use o botão verde para abrir em nova aba.'
            : 'Não foi possível fazer o download. Use o botão verde para abrir em nova aba.';
        message.error(errorMessage);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenFileInNewTab = async () => {
    try {
      // Open file immediately - don't wait for API
      if (data.file_url) {
        window.open(data.file_url, '_blank');
      }

      // Update state immediately to mark as viewed
      const updatedFile = {
        ...data,
        viewed_at: moment().format('L LTS'),
        viewed_by: user!.id,
      };
      
      updateFiles(updatedFile);
      setModalData(updatedFile);

      // Show feedback modal if first time viewing
      const feedbackHasBeenDisplayed =
        localStorage.getItem(LOCAL_STORAGE.FEEDBACK_HAS_BEEN_DISPLAYED) === 'true';

      if (!data.viewed_at && !feedbackHasBeenDisplayed) {
        openFeedbackModal(data);
        localStorage.setItem(LOCAL_STORAGE.FEEDBACK_HAS_BEEN_DISPLAYED, 'true');
      }

      // Mark as viewed in background (async, don't wait for response)
      markFileAsViewed(data.id)
        .then((response) => {
          // API succeeded - state already updated above
          logger.log('File marked as viewed successfully');
        })
        .catch((error) => {
          // Log error but don't show to user since file already opened and state updated
          logger.error('Error marking file as viewed:', error);
          logErrorToSentry(error, {
            action: 'markFileAsViewed',
          });
        });
    } catch (error) {
      logger.error('Error opening file in new tab:', error);
      message.error('Não foi possível abrir o arquivo. Verifique se o pop-up foi bloqueado.');
    }
  };

  return (
    <ModalStyled
      title={data.description}
      open={isVisible}
      onCancel={handleModalClose}
      footer={false}
      destroyOnClose
      centered
      width={520}
    >
      <InfoGrid>
        {isPaymentRequired(data.requires_payment) && (
          <InfoItem $color="var(--danger)">
            <InfoIcon $color="var(--danger)">
              <AiOutlineCalendar />
            </InfoIcon>
            <InfoContent>
              <InfoLabel>Data de Vencimento</InfoLabel>
              <InfoValue>{moment(data.due_at).format('DD/MM/YYYY')}</InfoValue>
            </InfoContent>
          </InfoItem>
        )}

        <InfoItem $color="var(--info)">
          <InfoIcon $color="var(--info)">
            <BsCalendarCheck />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>Competência</InfoLabel>
            <InfoValue>{moment(data.competency_at).format('DD/MM/YYYY')}</InfoValue>
          </InfoContent>
        </InfoItem>

        {isPaymentRequired(data.requires_payment) && Number(data.amount) > 0 && (
          <InfoItem $color="var(--success)">
            <InfoIcon $color="var(--success)">
              <AiOutlineDollarCircle />
            </InfoIcon>
            <InfoContent>
              <InfoLabel>Valor</InfoLabel>
              <InfoValue>{brlCurrencyFormat(data.amount)}</InfoValue>
            </InfoContent>
          </InfoItem>
        )}

        <InfoItem $color="var(--primary)">
          <InfoIcon $color="var(--primary)">
            <AiOutlineFolder />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>Departamento</InfoLabel>
            <InfoValue>{getDepartmentName(data.department)}</InfoValue>
          </InfoContent>
        </InfoItem>

        <InfoItem className="full-width">
          <DownloadContainer>
            {!downloadFailed && (
              <DownloadButton
                onClick={handleDownloadFile}
                loading={isDownloading}
                disabled={isDownloading}
              >
                <AiOutlineDownload />
                {isDownloading ? 'Baixando arquivo...' : 'Baixar Arquivo'}
              </DownloadButton>
            )}
            
            {data.file_url && (
              <OpenFileButton
                onClick={handleOpenFileInNewTab}
                disabled={isDownloading}
              >
                <HiOutlineExternalLink />
                Abrir Arquivo em Nova Aba
              </OpenFileButton>
            )}
            
            {data.viewed_at && (
              <AlreadyDownloadedText>
                Arquivo já visualizado anteriormente
              </AlreadyDownloadedText>
            )}
            
            {downloadFailed && (
              <ErrorInstructions>
                <ErrorTitle>Navegador bloqueou o download</ErrorTitle>
                <ErrorMessage>
                  Use o botão verde acima para abrir o arquivo diretamente no navegador.
                </ErrorMessage>
              </ErrorInstructions>
            )}
          </DownloadContainer>
        </InfoItem>
      </InfoGrid>

      {data.department &&
        user?.id &&
        user.tenant_phones[data.department] && (
          <WhatsappContainer>
            <WhatsappText>Está com dúvidas? Fale conosco!</WhatsappText>
            <WhatsappLink
              href={`https://wa.me/55${
                user.tenant_phones[data.department]
              }`}
            >
              <WhatsappImage />
              Enviar mensagem no WhatsApp
            </WhatsappLink>
          </WhatsappContainer>
        )}
    </ModalStyled>
  );
};
